/**
 * Assistant Nudge Hook
 *
 * Provides contextual nudge data for the AI assistant button:
 * - Notification dot (has important alerts)
 * - Nudge balloon text (contextual tip above button)
 * - Mini-card for empty pages
 *
 * Uses lightweight queries to avoid performance impact.
 */

import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getSupabase } from '@/lib/supabase';
import { useAuthReady } from '@/hooks/useAuthReady';

interface NudgeData {
  /** Whether to show notification dot on AI button */
  hasAlert: boolean;
  /** Alert type for dot color */
  alertType: 'urgent' | 'warning' | 'info' | null;
  /** Nudge balloon text to show above button (null = hide balloon) */
  nudgeText: string | null;
  /** Mini-card data for empty pages */
  miniCard: { text: string; action: string; icon: string } | null;
}

interface QuickStats {
  propertiesCount: number;
  unitsCount: number;
  bookingsCount: number;
  unreadMessagesCount: number;
  hasWifi: boolean;
  hasCheckInInstructions: boolean;
  hasImages: boolean;
  nextCheckIn: string | null;
  nextGuestName: string | null;
  nextUnitName: string | null;
  hostUpdatesCount: number;
}

const NUDGE_KEY = 'assistant-nudge-stats';
// Data the nudges summarise. Whenever any of these lists is invalidated (a
// property/unit/booking was created, edited, trashed or restored — from ANY
// hook or the setup wizard) the nudge refetches too, instead of showing a stale
// "Add your first property!" for minutes after one was just created.
const WATCHED_KEYS = new Set(['properties', 'units', 'bookings', 'trash']);

interface NudgePropertyRow {
  id: string;
  default_wifi_name: string | null;
  default_check_in_instructions: string | null;
}
interface NudgeUnitRow {
  id: string;
  name: string | null;
  property_id: string;
  wifi_name: string | null;
  check_in_instructions: string | null;
  images: string[] | null;
  inherit_wifi: boolean | null;
  inherit_checkin_instructions: boolean | null;
}

function useQuickStats(): { data: QuickStats | null; isLoading: boolean } {
  const { userId, isReady } = useAuthReady();
  const queryClient = useQueryClient();

  useEffect(() => {
    return queryClient.getQueryCache().subscribe((event) => {
      if (event.type !== 'updated' || event.action.type !== 'invalidate') return;
      const root = event.query.queryKey[0];
      if (typeof root === 'string' && WATCHED_KEYS.has(root)) {
        queryClient.invalidateQueries({ queryKey: [NUDGE_KEY] });
      }
    });
  }, [queryClient]);

  const result = useQuery({
    queryKey: [NUDGE_KEY, userId],
    queryFn: async (): Promise<QuickStats> => {
      const supabase = getSupabase();
      if (!userId) throw new Error('No user');

      const today = new Date().toISOString().split('T')[0];

      const [
        propertiesRes,
        unitsRes,
        bookingsRes,
        unreadRes,
        nextBookingRes,
        hostUpdatesRes,
      ] = await Promise.all([
        // Property defaults are needed too: units INHERIT WiFi / arrival
        // instructions from their property (the setup wizard saves them there),
        // so checking only the unit's own columns falsely nagged "WiFi not set".
        // Trashed rows (deleted_at) don't count.
        supabase.from('properties').select('id, default_wifi_name, default_check_in_instructions', { count: 'exact' }).eq('landlord_id', userId).is('deleted_at', null),
        supabase.from('units').select('id, name, property_id, wifi_name, check_in_instructions, images, inherit_wifi, inherit_checkin_instructions', { count: 'exact' }).eq('is_active', true).is('deleted_at', null),
        supabase.from('bookings').select('id', { count: 'exact', head: true }).eq('landlord_id', userId).is('deleted_at', null),
        supabase.from('messages').select('id', { count: 'exact', head: true }).eq('sender_type', 'guest').eq('is_read', false),
        supabase.from('bookings').select('guest_name, check_in, unit_id').eq('landlord_id', userId).gte('check_in', today).in('status', ['confirmed', 'pending']).order('check_in', { ascending: true }).limit(1),
        supabase.from('host_updates').select('id', { count: 'exact', head: true }).eq('landlord_id', userId).eq('is_active', true),
      ]);

      const properties = (propertiesRes.data || []) as NudgePropertyRow[];
      const propertyById = new Map(properties.map((p) => [p.id, p]));
      // Only units of live (non-trashed) properties count.
      const units = ((unitsRes.data || []) as NudgeUnitRow[]).filter((u) => propertyById.has(u.property_id));
      const nextBooking = nextBookingRes.data?.[0] || null;
      // Effective value = the unit's own, or the property default when inheriting
      // (inherit flags default to true when NULL, matching the guest portal).
      const effective = (
        own: string | null,
        inheritFlag: boolean | null,
        propertyId: string,
        propKey: 'default_wifi_name' | 'default_check_in_instructions',
      ) => own || (inheritFlag !== false ? propertyById.get(propertyId)?.[propKey] : null);

      // Get unit name for next booking
      let nextUnitName: string | null = null;
      if (nextBooking?.unit_id && units.length > 0) {
        const unit = units.find((u) => u.id === nextBooking.unit_id);
        nextUnitName = unit?.name || null;
      }
      // If not found in loaded units, do a quick lookup
      if (nextBooking?.unit_id && !nextUnitName) {
        const { data: unitRow } = await supabase.from('units').select('name').eq('id', nextBooking.unit_id).single();
        nextUnitName = unitRow?.name || null;
      }

      return {
        propertiesCount: properties.length,
        unitsCount: units.length,
        bookingsCount: bookingsRes.count || 0,
        unreadMessagesCount: unreadRes.count || 0,
        hasWifi: units.some((u) => !!effective(u.wifi_name, u.inherit_wifi, u.property_id, 'default_wifi_name')),
        hasCheckInInstructions: units.some((u) => !!effective(u.check_in_instructions, u.inherit_checkin_instructions, u.property_id, 'default_check_in_instructions')),
        hasImages: units.some((u) => (u.images?.length || 0) > 0),
        nextCheckIn: nextBooking?.check_in || null,
        nextGuestName: nextBooking?.guest_name || null,
        nextUnitName,
        hostUpdatesCount: hostUpdatesRes.count || 0,
      };
    },
    enabled: isReady,
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchInterval: 5 * 60 * 1000, // 5 minutes
  });

  return { data: result.data ?? null, isLoading: result.isLoading };
}

export function useAssistantNudge(): NudgeData {
  const { data: stats, isLoading } = useQuickStats();
  const location = useLocation();
  const { t } = useTranslation();
  const path = location.pathname;

  return useMemo(() => {
    const result: NudgeData = {
      hasAlert: false,
      alertType: null,
      nudgeText: null,
      miniCard: null,
    };

    if (isLoading || !stats) return result;

    // ──── Calculate days until next check-in ────
    let daysUntilCheckIn = Infinity;
    if (stats.nextCheckIn) {
      const checkInDate = new Date(stats.nextCheckIn);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      daysUntilCheckIn = Math.ceil((checkInDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    }

    // ──── 1. NOTIFICATION DOT ────
    // Urgent: guest arriving today/tomorrow
    if (daysUntilCheckIn <= 1) {
      result.hasAlert = true;
      result.alertType = 'urgent';
    }
    // Warning: unread messages
    else if (stats.unreadMessagesCount > 0) {
      result.hasAlert = true;
      result.alertType = 'warning';
    }
    // Info: missing critical setup
    else if (stats.unitsCount > 0 && (!stats.hasWifi || !stats.hasCheckInInstructions)) {
      result.hasAlert = true;
      result.alertType = 'info';
    }

    // ──── 2. NUDGE BALLOON (rotate based on priority) ────
    // Only show when assistant is closed, pick the most important one
    if (daysUntilCheckIn === 0) {
      const name = stats.nextGuestName?.split(' ')[0] || '';
      result.nudgeText = t('assistant.nudge.arrivesToday', { name });
    } else if (daysUntilCheckIn === 1) {
      const name = stats.nextGuestName?.split(' ')[0] || '';
      result.nudgeText = t('assistant.nudge.arrivesTomorrow', { name });
    } else if (stats.unreadMessagesCount > 0) {
      result.nudgeText = t('assistant.nudge.unreadMessages', { count: stats.unreadMessagesCount });
    } else if (stats.unitsCount > 0 && !stats.hasWifi) {
      result.nudgeText = t('assistant.nudge.wifiNotSet');
    } else if (stats.unitsCount > 0 && !stats.hasCheckInInstructions) {
      result.nudgeText = t('assistant.nudge.addCheckInInstructions');
    } else if (daysUntilCheckIn <= 7 && daysUntilCheckIn > 1) {
      const name = stats.nextGuestName?.split(' ')[0] || '';
      result.nudgeText = t('assistant.nudge.arrivesInDays', { name, days: daysUntilCheckIn });
    }

    // ──── 3. MINI-CARDS for empty pages ────
    if (path === '/dashboard/properties' && stats.propertiesCount === 0) {
      result.miniCard = {
        text: t('assistant.nudge.miniAddProperty'),
        action: '/dashboard/properties/new',
        icon: '🏠',
      };
    } else if (path.match(/\/dashboard\/properties\/[^/]+$/) && stats.unitsCount === 0) {
      result.miniCard = {
        text: t('assistant.nudge.miniAddUnit'),
        action: 'add-unit',
        icon: '🛏️',
      };
    } else if (path === '/dashboard/bookings' && stats.bookingsCount === 0) {
      result.miniCard = {
        text: t('assistant.nudge.miniCreateBooking'),
        action: '/dashboard/bookings/new',
        icon: '📅',
      };
    } else if (path === '/dashboard/host-updates' && stats.hostUpdatesCount === 0) {
      result.miniCard = {
        text: t('assistant.nudge.miniPostUpdate'),
        action: 'new-update',
        icon: '✨',
      };
    } else if (path === '/dashboard/local-guide' && stats.propertiesCount > 0) {
      // Don't show mini-card for local guide unless we know it's empty
      // (would need separate query, skip for now)
    }

    return result;
  }, [stats, isLoading, path, t]);
}
